import { useCallback, useEffect, useState } from "react"
import {
  Stream,
  User,
  TeamMember,
  getStreams,
  getTeamStats,
  postLogout,
} from "./api"
import moment from "moment"
import {
  summarizeCallouts,
  summarizeClassesCounts,
  summarizeCountryCounts,
} from "./utils"

const regionNamesInEnglish = new Intl.DisplayNames(["en"], { type: "region" })

const REFRESH_STREAM_INTERVAL_SECONDS = 60
const REFRESH_STATS_INTERVAL_SECONDS = 5

export const DataScreen = ({
  loggedInUser,
  onLoggedOut,
}: {
  loggedInUser: User
  onLoggedOut: () => void
}) => {
  const [logoutError, setLogoutError] = useState<string>()
  const [refreshError, setRefreshError] = useState<string>()
  const [stream, setStream] = useState<Stream>()
  const [teamStats, setTeamStats] = useState<TeamMember[]>([])

  const countryCounts = summarizeCountryCounts(teamStats)
  const classesCounts = summarizeClassesCounts(teamStats)
  const { birthdays, newbies, achievements } = summarizeCallouts(teamStats)

  const searchParams = new URLSearchParams(window.location.search)
  const debugStarUserId = searchParams.get("debug_star_user_id")
  const starUserId = debugStarUserId ? Number(debugStarUserId) : loggedInUser.id

  const refreshStream = useCallback(async () => {
    try {
      const streams = await getStreams()
      const stream = streams.find(
        (stream) =>
          !stream.replay &&
          stream.star_user_id === starUserId &&
          moment(stream.start_utc_iso_datetime)
            .add(stream.duration_s, "seconds")
            .isAfter(moment()),
      )
      if (
        stream !== undefined &&
        moment(stream.start_utc_iso_datetime).isBefore(moment().add(1, "hours"))
      ) {
        setStream(stream)
      } else {
        setStream(undefined)
      }
      setRefreshError(undefined)
    } catch {
      setRefreshError("Something went wrong, automatically retrying.")
    }
  }, [starUserId])

  useEffect(() => {
    refreshStream()
    const interval = setInterval(
      refreshStream,
      REFRESH_STREAM_INTERVAL_SECONDS * 1000,
    )
    return () => clearInterval(interval)
  }, [refreshStream])

  const refreshTeamStats = useCallback(async () => {
    if (stream?.stream_uuid) {
      try {
        const teamStats = await getTeamStats(stream.stream_uuid)
        setTeamStats(teamStats)
        setRefreshError(undefined)
      } catch {
        setRefreshError("Something went wrong, automatically retrying.")
      }
    }
  }, [stream?.stream_uuid])

  useEffect(() => {
    refreshTeamStats()
    const interval = setInterval(
      refreshTeamStats,
      REFRESH_STATS_INTERVAL_SECONDS * 1000,
    )
    return () => clearInterval(interval)
  }, [refreshTeamStats])

  const logout = useCallback(async () => {
    setLogoutError(undefined)
    try {
      await postLogout()
      onLoggedOut()
    } catch {
      setLogoutError("Something went wrong, please try again.")
    }
  }, [onLoggedOut])

  const getFlagEmoji = (countryCode: string) => {
    return countryCode
      .toUpperCase()
      .replace(/./g, (char) =>
        String.fromCodePoint(127397 + char.charCodeAt(0)),
      )
  }

  const getClassesTitle = (classes: number) => {
    if (classes === 1) {
      return "First class"
    } else if (classes === 10) {
      return "2+ classes"
    } else if (classes === 100) {
      return "10+ classes"
    } else {
      return "100+ classes"
    }
  }

  const getBarWidth = (count: number) => {
    const ratio = teamStats.length === 0 ? 0 : count / teamStats.length
    return `${ratio * 100}%`
  }

  return (
    <>
      <div className="Data-header">
        <div className="class">
          {stream !== undefined ? (
            <>
              {stream.concept.title} with {stream.star.name} (
              {Math.round(stream.duration_s / 60)} min)
            </>
          ) : (
            <>No class in the next hour</>
          )}
        </div>
        {logoutError !== undefined && (
          <div className="App-api-error">{logoutError}</div>
        )}
        {refreshError !== undefined && (
          <div className="App-api-error">{refreshError}</div>
        )}
        <div className="user">
          {loggedInUser.email}
          <span className="pipe">|</span>
          <button className="logout" onClick={logout}>
            Log out
          </button>
        </div>
      </div>
      <div className="Data-body">
        <div className="card">
          <div className="title">Countries</div>
          <div className="grid">
            {countryCounts.map(({ countryCode, count }) => (
              <div className="item">
                <div className="count">{count}</div>
                <div className="value-container">
                  <div className="value">
                    {countryCode !== undefined
                      ? `${regionNamesInEnglish.of(countryCode)} ${getFlagEmoji(
                          countryCode,
                        )}`
                      : "Other"}
                  </div>
                  <div className="bar-container">
                    <div
                      className="bar"
                      style={{ width: getBarWidth(count) }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="card">
          <div className="title">Experience</div>
          <div className="grid">
            {classesCounts.map(({ classes, count }) => (
              <div className="item">
                <div className="count">{count}</div>
                <div className="value-container">
                  <div className="value">{getClassesTitle(classes)}</div>
                  <div className="bar-container">
                    <div
                      className="bar"
                      style={{ width: getBarWidth(count) }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="card">
          <div className="title">Callouts</div>
          <div className="callouts">
            <div className="value">
              Birthdays: <span>{birthdays.join(", ")}</span>
            </div>
            <div className="value">
              Newbies: <span>{newbies.join(", ")}</span>
            </div>
            <div className="value">
              Achievements:{" "}
              <span>
                {achievements
                  .map(
                    ({ nickname, n_sessions }) =>
                      `${nickname} (${n_sessions} classes)`,
                  )
                  .join(", ")}
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
